
import React, { createContext, useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/context/SupabaseAuthContext';
import { User } from '@/types/userRole';

// Define the login form schema
const loginFormSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
  captcha: z.string().optional(),
  rememberMe: z.boolean().default(false),
  termsAccepted: z.boolean().default(false),
});

export type LoginFormValues = z.infer<typeof loginFormSchema>;

// Context interface
interface LoginFormContextInterface {
  form: ReturnType<typeof useForm<LoginFormValues>>;
  isLoading: boolean;
  captchaValue: string;
  setCaptchaValue: (value: string) => void;
  handleCaptchaGenerate: (text: string) => void;
  onSubmit: (data: LoginFormValues) => Promise<void>;
}

// Create the context
const LoginFormContext = createContext<LoginFormContextInterface | undefined>(undefined);

export interface LoginFormProviderProps {
  children: React.ReactNode;
  redirectPath?: string;
  showCaptcha?: boolean;
  showTerms?: boolean;
  onLoginSuccess?: (user: User) => void;
  isSaasInstance?: boolean;
  isLoading?: boolean; // Add isLoading prop to the interface
}

export const LoginFormProvider: React.FC<LoginFormProviderProps> = ({
  children,
  redirectPath = '/dashboard',
  showCaptcha = false,
  showTerms = false,
  onLoginSuccess,
  isSaasInstance = false,
  isLoading: externalIsLoading, // Accept isLoading prop from parent
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn } = useAuth();
  const [isLoading, setIsLoading] = useState(externalIsLoading || false); // Use external isLoading if provided
  const [captchaValue, setCaptchaValue] = useState('');

  // Create form
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
      captcha: '',
      rememberMe: false,
      termsAccepted: false,
    },
  });

  // Handle captcha generation
  const handleCaptchaGenerate = (text: string) => {
    setCaptchaValue(text);
    form.setValue('captcha', text);
  };

  // Handle login (directly usable by consumers)
  const handleLogin = async (data: LoginFormValues): Promise<void> => {
    if (!externalIsLoading) { // Only set loading state if not externally controlled
      setIsLoading(true);
    }
    
    try {
      // Call Supabase signIn
      const { error } = await signIn(data.email, data.password);
      
      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: 'Login Successful',
        description: 'You have been successfully logged in.',
      });

      if (onLoginSuccess) {
        // Create a mock user object for backward compatibility
        const mockUser: User = {
          id: 'temp-id',
          name: 'User',
          email: data.email,
          role: 'user',
          createdAt: new Date()
        };
        onLoginSuccess(mockUser);
      }

      // Navigate to dashboard or specified redirect path
      navigate(redirectPath);
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: 'Login Failed',
        description: error instanceof Error ? error.message : 'Invalid credentials',
        variant: 'destructive',
      });
      throw error;
    } finally {
      if (!externalIsLoading) { // Only reset loading state if not externally controlled
        setIsLoading(false);
      }
    }
  };

  // Handle form submission
  const onSubmit = async (data: LoginFormValues): Promise<void> => {
    if (showCaptcha && data.captcha !== captchaValue) {
      form.setError('captcha', {
        type: 'manual',
        message: 'Invalid captcha code',
      });
      return;
    }

    if (showTerms && !data.termsAccepted) {
      form.setError('termsAccepted', {
        type: 'manual',
        message: 'You must accept the terms and conditions',
      });
      return;
    }

    try {
      await handleLogin(data);
    } catch (error) {
      // Error is already handled in handleLogin
    }
  };

  // Use the externally provided isLoading state if available
  const currentIsLoading = externalIsLoading !== undefined ? externalIsLoading : isLoading;

  const value = {
    form,
    isLoading: currentIsLoading,
    captchaValue,
    setCaptchaValue,
    handleCaptchaGenerate,
    onSubmit
  };

  return <LoginFormContext.Provider value={value}>{children}</LoginFormContext.Provider>;
};

export const useLoginForm = (): LoginFormContextInterface => {
  const context = useContext(LoginFormContext);
  if (context === undefined) {
    throw new Error('useLoginForm must be used within a LoginFormProvider');
  }
  return context;
};
